/**
 * Proyecto: MOGOTAX-CONDUCTOR
 * WebSocket nativo para la app de conductores
 * Maneja actualizaciones en tiempo real de traslados asignados
 */
class NativeWebSocket {
  constructor() {
    this.ws = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 20;
    this.reconnectDelay = 1000;
    this.maxReconnectDelay = 30000;
    this.heartbeatInterval = null;
    this.lastHeartbeat = null;
    this.connectionState = 'disconnected'; // 'connected', 'connecting', 'disconnected', 'error'
    this.statusCallbacks = [];
    this.connectionStartTime = null;
    this.conductorId = null;
    this.vehiculoIds = null; // administrador de vehículos: filtra por sus vehículos
  }

  /**
   * Filtro de eventos de la sesión actual. Conductor: sus traslados. Administrador de vehículos: los de
   * sus vehículos (lista vacía → ninguno). Se puede cambiar sin reconectar (cambio de perfil).
   */
  setFiltro(conductorId = null, vehiculoIds = null) {
    this.conductorId = conductorId;
    this.vehiculoIds = Array.isArray(vehiculoIds) ? vehiculoIds.map(Number) : null;
  }

  /**
   * Conectar al servidor WebSocket
   * @param {number} conductorId - ID del conductor para filtrar eventos
   * @param {number[]} vehiculoIds - vehículos del administrador (en lugar del conductor)
   */
  connect(conductorId = null, vehiculoIds = this.vehiculoIds) {
    this.setFiltro(conductorId, vehiculoIds);

    // Evitar múltiples conexiones
    if (this.ws && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)) {
      console.log('↩️ WebSocket ya está conectado o conectando');
      return;
    }

    // Limpiar cualquier WebSocket existente
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      this.ws = null;
    }

    // Construir URL del WebSocket
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const host = window.location.hostname;

    let wsUrl;
    if (window.location.protocol === 'https:') {
      // En producción HTTPS, conectar vía proxy Nginx
      wsUrl = `${protocol}://${host}/app/mogotax-app-key?protocol=7&client=js&version=1.0.0`;
    } else {
      // En desarrollo HTTP, conectar directamente al puerto 8082
      wsUrl = `${protocol}://${host}:8082/app/mogotax-app-key?protocol=7&client=js&version=1.0.0`;
    }

    this.updateConnectionState('connecting');
    this.connectionStartTime = Date.now();

    try {
      this.ws = new WebSocket(wsUrl);
      let connectionTimeout = null;

      // Timeout para conexión inicial
      connectionTimeout = setTimeout(() => {
        if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
          console.warn('⚠️ Timeout de conexión WebSocket');
          this.ws.close();
        }
      }, 15000);

      this.ws.onopen = () => {
        clearTimeout(connectionTimeout);
        const connectionTime = Date.now() - this.connectionStartTime;
        console.log(`✅ WebSocket conectado en ${connectionTime}ms`);

        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        this.updateConnectionState('connected');

        // Suscribirse al canal de traslados
        this.subscribe('traslados');

        // Iniciar heartbeat
        this.startHeartbeat();
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (e) {
          console.warn('⚠️ Error parseando mensaje WebSocket:', e);
        }
      };

      this.ws.onclose = () => {
        this.updateConnectionState('disconnected');
        this.stopHeartbeat();
        this.attemptReconnect();
      };

      this.ws.onerror = (error) => {
        console.error('❌ WebSocket ERROR:', error);
        this.updateConnectionState('error');
      };

    } catch (error) {
      console.error('❌ Error creando WebSocket:', error);
      this.attemptReconnect();
    }
  }

  /**
   * Suscribirse a un canal
   */
  subscribe(channel) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const subscribeMessage = {
        event: 'pusher:subscribe',
        data: {
          channel: channel
        }
      };

      console.log(`📢 Suscribiéndose al canal: ${channel}`);
      this.ws.send(JSON.stringify(subscribeMessage));
    } else {
      console.warn(`⚠️ No se pudo suscribir al canal ${channel}, WebSocket no está abierto`);
    }
  }

  /**
   * Manejar mensajes entrantes
   */
  handleMessage(data) {
    const { event } = data;

    switch (event) {
      case 'traslado.simple.update':
        this.handleTrasladoUpdate(data.data);
        break;
      case 'traslado.simple.created':
        this.handleTrasladoCreated(data.data);
        break;
      case 'traslado_estado_cambiado':
        this.handleTrasladoStatusChange(data.data);
        break;
      case 'traslado.actualizado':
        this.handleTrasladoActualizado(data.data);
        break;
      case 'traslado.asignado':
        this.handleTrasladoAsignado(data.data);
        break;
      case 'traslado.cancelado':
        this.handleTrasladoCancelado(data.data);
        break;
      case 'pusher:pong':
        this.lastHeartbeat = Date.now();
        break;
      case 'pusher_internal:subscription_succeeded':
        console.log('✅ Suscripción exitosa al canal');
        break;
      default:
        // Evento no manejado
        break;
    }
  }

  /**
   * Parsear datos de evento
   */
  parseData(data) {
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch (e) {
        console.error('Error parseando datos:', e);
        return null;
      }
    }
    return data;
  }

  /**
   * Verificar si el traslado pertenece al conductor actual
   */
  isForCurrentConductor(traslado) {
    if (this.vehiculoIds) return this.vehiculoIds.includes(Number(traslado?.vehiculo_id));
    if (!this.conductorId) return true; // Si no hay conductor, aceptar todos
    return traslado?.conductor_id === this.conductorId ||
           traslado?.conductorId === this.conductorId;
  }

  handleTrasladoUpdate(data) {
    const parsedData = this.parseData(data);
    if (!parsedData) return;

    // Filtrar por conductor si está configurado
    if (parsedData.traslado && !this.isForCurrentConductor(parsedData.traslado)) {
      return;
    }

    this.emitEvent('traslado.update', parsedData);
  }

  handleTrasladoCreated(data) {
    const parsedData = this.parseData(data);
    if (!parsedData) return;

    if (parsedData.traslado && !this.isForCurrentConductor(parsedData.traslado)) {
      return;
    }

    this.emitEvent('traslado.created', parsedData);
  }

  handleTrasladoStatusChange(data) {
    const parsedData = this.parseData(data);
    if (!parsedData) return;

    const { trasladoId, traslado } = parsedData;

    if (!trasladoId || !traslado) {
      console.warn('⚠️ Datos incompletos en cambio de estado:', parsedData);
      return;
    }

    if (!this.isForCurrentConductor(traslado)) {
      return;
    }

    this.emitEvent('traslado.status.change', { trasladoId, traslado });
  }

  handleTrasladoActualizado(data) {
    const parsedData = this.parseData(data);
    if (!parsedData) return;

    const { trasladoId, traslado, changes } = parsedData;

    if (!trasladoId || !traslado) {
      console.warn('⚠️ Datos incompletos en actualización:', parsedData);
      return;
    }

    if (!this.isForCurrentConductor(traslado)) {
      return;
    }

    this.emitEvent('traslado.actualizado', { trasladoId, traslado, changes });
  }

  handleTrasladoAsignado(data) {
    const parsedData = this.parseData(data);
    if (!parsedData) return;

    const { traslado } = parsedData;

    // Este evento es importante: nuevo traslado asignado al conductor
    if (traslado && this.isForCurrentConductor(traslado)) {
      console.log('🚀 Nuevo traslado asignado:', traslado.id);
      this.emitEvent('traslado.asignado', parsedData);
    }
  }

  handleTrasladoCancelado(data) {
    const parsedData = this.parseData(data);
    if (!parsedData) return;

    const { traslado } = parsedData;

    if (traslado && this.isForCurrentConductor(traslado)) {
      console.log('❌ Traslado cancelado:', traslado.id);
      this.emitEvent('traslado.cancelado', parsedData);
    }
  }

  /**
   * Emitir evento a los listeners registrados
   */
  emitEvent(eventName, data) {
    const callbacks = this.listeners.get(eventName) || [];
    callbacks.forEach(callback => {
      try {
        callback(data);
      } catch (e) {
        console.error(`Error en callback de ${eventName}:`, e);
      }
    });
  }

  // ==========================================
  // Registro de Listeners
  // ==========================================

  onTrasladoUpdate(callback) {
    this.addListener('traslado.update', callback);
    return () => this.removeListener('traslado.update', callback);
  }

  onTrasladoCreated(callback) {
    this.addListener('traslado.created', callback);
    return () => this.removeListener('traslado.created', callback);
  }

  onTrasladoStatusChange(callback) {
    this.addListener('traslado.status.change', callback);
    return () => this.removeListener('traslado.status.change', callback);
  }

  onTrasladoActualizado(callback) {
    this.addListener('traslado.actualizado', callback);
    return () => this.removeListener('traslado.actualizado', callback);
  }

  onTrasladoAsignado(callback) {
    this.addListener('traslado.asignado', callback);
    return () => this.removeListener('traslado.asignado', callback);
  }

  onTrasladoCancelado(callback) {
    this.addListener('traslado.cancelado', callback);
    return () => this.removeListener('traslado.cancelado', callback);
  }

  addListener(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    const listeners = this.listeners.get(event);
    if (!listeners.includes(callback)) {
      listeners.push(callback);
    }
  }

  removeListener(event, callback) {
    if (this.listeners.has(event)) {
      const listeners = this.listeners.get(event);
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  // ==========================================
  // Heartbeat y Reconexión
  // ==========================================

  startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        const pingMessage = {
          event: 'pusher:ping',
          data: {}
        };

        try {
          this.ws.send(JSON.stringify(pingMessage));
          this.lastHeartbeat = Date.now();
        } catch (error) {
          console.error('❌ Error enviando ping:', error);
          this.ws.close();
        }
      }
    }, 25000);
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  attemptReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;

      // Backoff exponencial con jitter
      const baseDelay = Math.min(this.reconnectDelay * Math.pow(1.5, this.reconnectAttempts - 1), this.maxReconnectDelay);
      const jitter = Math.random() * 1000;
      const delay = Math.floor(baseDelay + jitter);

      console.log(`🔄 Reintento ${this.reconnectAttempts}/${this.maxReconnectAttempts} en ${delay}ms`);

      setTimeout(() => {
        this.connect(this.conductorId);
      }, delay);
    } else {
      console.error('❌ Máximo de reintentos alcanzado');
      this.updateConnectionState('error');
    }
  }

  // ==========================================
  // Estado de Conexión
  // ==========================================

  disconnect() {
    this.stopHeartbeat();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.updateConnectionState('disconnected');
  }

  updateConnectionState(newState) {
    this.connectionState = newState;
    this.statusCallbacks.forEach(callback => {
      try {
        callback(newState);
      } catch (e) {
        console.error('Error en callback de estado:', e);
      }
    });
  }

  onStatusChange(callback) {
    if (!this.statusCallbacks.includes(callback)) {
      this.statusCallbacks.push(callback);
    }
    callback(this.connectionState);
    return () => {
      const index = this.statusCallbacks.indexOf(callback);
      if (index > -1) {
        this.statusCallbacks.splice(index, 1);
      }
    };
  }

  getConnectionState() {
    return this.connectionState;
  }

  isConnected() {
    return this.connectionState === 'connected';
  }
}

// Crear instancia singleton
const nativeWebSocket = new NativeWebSocket();

export default nativeWebSocket;
